import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Button, Loader, Typography } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { getMessageKey } from '../../../utils/utils';
import {
  REQUEST_TYPE_ACCOUNT,
  REQUEST_TYPE_ENTITLEMENT,
  REQUEST_TYPE_ROLE,
} from '../constant';
import { getArsKind } from '../utils';

import ARSApproverTile from './ARSApproverTile';
import ARSRequestTile from './ARSRequestTile';
import ARSTaskTile from './ARSTaskTile';

function AccessRequestHistoryBody({
  isError,
  isLoading,
  data,
  arsTaskPayload,
  closeAccessRequestHistoryModal,
  reTriggerAccessRequestHistoryModal,
  messageKeys,
  globalDateFormat,
}) {
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);
  const hasDataAvailable = Boolean(data?.requestHistoryFound && !isLoading);

  const getArsObject = () => {
    const arsKind = getArsKind(arsTaskPayload);

    if (arsKind === REQUEST_TYPE_ROLE) {
      return 'role';
    }

    if (arsKind === REQUEST_TYPE_ACCOUNT) {
      return 'account';
    }

    if (arsKind === REQUEST_TYPE_ENTITLEMENT) {
      return 'entitlement';
    }

    return '';
  };

  const requestTileData = hasDataAvailable && {
    arsRequestSectionLabelKey: data?.arsRequestSectionLabelKey,
    arsRequestSectionLabelValue: data?.arsRequestSectionLabelValue,
    arsRequestMap: data?.arsRequestMap,
    messageKeys,
  };
  const approverTileData = hasDataAvailable && {
    accessApproverSectionLabelKey: data?.accessApproverSectionLabelKey,
    accessApproverSectionLabelValue: data?.accessApproverSectionLabelValue,
    reviewMethodLabelKey: data?.reviewMethodLabelKey,
    reviewMethodLabelValue: data?.reviewMethodLabelValue,
    reviewMethodMessageKey: data?.reviewMethodMessageKey,
    accessApproversSectionLabelKey: data?.accessApproversSectionLabelKey,
    accessApproversSectionLabelValue: data?.accessApproversSectionLabelValue,
    accessApprovers: data?.accessApprovers,
    messageKeys,
  };
  const taskSectionTileData = hasDataAvailable && {
    arsTaskSectionLabelKey: data?.arsTaskSectionLabelKey,
    arsTaskSectionLabeValue: data?.arsTaskSectionLabeValue,
    arsTaskLabelKey1: data?.arsTaskLabelKey1,
    arsTaskLabelKey2: data?.arsTaskLabelKey2,
    arsTaskSectionLabelValue: data?.arsTaskSectionLabelValue,
    arsTaskMap: data?.arsTaskMap,
    arsTaskList: data?.arsTaskList,
    messageKeys,
  };

  const renderArsModalBody = () => {
    if (isLoading) {
      return (
        <Box className='AccessRequestHistoryBody-wrapper'>
          <Loader
            kind='dots'
            format='inline'
            color='brand'
            size='large'
            className='AccessRequestHistoryBody-loader'
            dataTestId='AccessRequestHistoryBodyLoaderTestId'
          />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box className='AccessRequestHistoryBody-wrapper'>
          <div className='ReviewAndSubmitModal-bodyError'>
            <Typography kind='h2'>
              {messages['COULD_NOT_LOAD.ATTESTATIONS'] ||
                localize(intl, getMessageKey('COULD_NOT_LOAD'))}
            </Typography>
            <Typography kind='body2'>
              {messages['Failed.HISTORY.ATTESTATIONS'] ||
                localize(intl, getMessageKey('Failed.HISTORY'))}
            </Typography>
            <div className='ReviewAndSubmitModal-bodyErrorButtons'>
              <Button
                type='button'
                kind='filled'
                onClick={reTriggerAccessRequestHistoryModal}
                size='medium'>
                {messages['Refresh.ATTESTATIONS'] ||
                  localize(intl, getMessageKey('Refresh'))}
              </Button>
              <Button
                type='button'
                kind='ghost'
                size='medium'
                onClick={closeAccessRequestHistoryModal}>
                {messages['CloseLabel.ATTESTATIONS'] ||
                  localize(intl, getMessageKey('CloseLabel'))}
              </Button>
            </div>
          </div>
        </Box>
      );
    }

    if (hasDataAvailable) {
      return (
        <Box tag='div' className='AccessRequestHistoryBody-wrapper'>
          <ARSRequestTile
            requestTileData={requestTileData}
            approverTileData={approverTileData}
            globalDateFormat={globalDateFormat}
          />
          <ARSApproverTile
            approverTileData={approverTileData}
            taskSectionTileData={taskSectionTileData}
            globalDateFormat={globalDateFormat}
          />
          <ARSTaskTile
            taskSectionTileData={taskSectionTileData}
            globalDateFormat={globalDateFormat}
          />
        </Box>
      );
    }

    return (
      <Box tag='div' className='AccessRequestHistoryBody-wrapper'>
        <span className='AccessRequestHistoryBody-NoDataAvailable'>
          {`There is no request associated with this ${getArsObject()}.`}
        </span>
      </Box>
    );
  };

  return renderArsModalBody();
}

AccessRequestHistoryBody.propTypes = {
  isError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.shape({ requestHistoryFound: PropTypes.bool }),
  arsTaskPayload: PropTypes.shape({}),
  closeAccessRequestHistoryModal: PropTypes.func.isRequired,
  reTriggerAccessRequestHistoryModal: PropTypes.func.isRequired,
  messageKeys: PropTypes.shape({}).isRequired,
  globalDateFormat: PropTypes.string.isRequired,
};

AccessRequestHistoryBody.defaultProps = { data: null };

export default AccessRequestHistoryBody;
